// Fragment "affiliate" — the platform operator's affiliate console (super_admin/affiliate/**).
//
// It REPLACES the `aff.*` block that used to sit in superadmin/base.js. Those keys named a
// per-commission approve / mark-paid / reject workflow and a single editable "scale"; the
// v1 module has neither (payment is the wallet's rail, and rates are effective-dated rules
// with a history), so not one of them survives. The `saff.` prefix is the contract's own
// (§7: `aff.*` for the practitioner, `saff.*` for the operator) and it is what lets the two
// planes say the same refusal to different readers without either dictionary owning the
// other's wording.
//
// Product-type labels are NOT duplicated here: the rules editor reuses the gateway
// console's `gw.productType.*`, which already names the same four natures in this very
// namespace.
export default {
  fr: {
    "nav.affiliate": "Affiliation",

    "saff.title": "Programme d'affiliation",
    "saff.subtitle": "Barème, affiliés, commissions et reprises. Chaque commission est une écriture équilibrée du grand livre, payée par le portefeuille.",
    "saff.loading": "Chargement…",

    "saff.tab.overview": "Vue d'ensemble",
    "saff.tab.affiliates": "Affiliés",
    "saff.tab.commissions": "Commissions",
    "saff.tab.rules": "Barème",
    "saff.tab.policy": "Paramètres",
    "saff.tab.legacy": "Historique v0",

    "saff.overview.affiliates": "Affiliés",
    "saff.overview.referralsTotal": "Filleuls inscrits",
    "saff.overview.linked": "Liés, pas encore payants",
    "saff.overview.active": "Actifs, période en cours",
    "saff.overview.expired": "Période terminée",
    "saff.overview.voided": "Annulés",
    "saff.overview.accrued": "Commissions acquises",
    "saff.overview.onHold": "En attente de libération",
    "saff.overview.reversed": "Reprises",
    "saff.overview.clicks30d": "Clics (30 jours)",
    "saff.overview.note": "Tous ces chiffres sont recalculés à la lecture depuis le grand livre : aucun n'est un compteur stocké. Ce qu'un affilié peut retirer se lit dans son portefeuille.",

    "saff.affiliates.title": "Affiliés",
    "saff.affiliates.search": "Rechercher",
    "saff.affiliates.searchPlaceholder": "Code ou identifiant de cabinet (exact)",
    "saff.affiliates.filterAllStates": "Tous les statuts",
    "saff.affiliates.colPractice": "Cabinet",
    "saff.affiliates.colCode": "Code",
    "saff.affiliates.colStatus": "Statut",
    "saff.affiliates.colClicks": "Clics",
    "saff.affiliates.colReferrals": "Filleuls",
    "saff.affiliates.colAccrued": "Acquis",
    "saff.affiliates.colReversed": "Repris",
    "saff.affiliates.colCreatedAt": "Créé le",
    "saff.affiliates.colAction": "Action",
    "saff.affiliates.view": "Ouvrir",
    "saff.affiliates.empty": "Aucun affilié ne correspond.",

    "saff.accountState.active": "Actif",
    "saff.accountState.disabled": "Désactivé",

    "saff.detail.back": "Retour à la liste",
    "saff.detail.enable": "Réactiver ce compte",
    "saff.detail.disable": "Désactiver ce compte",
    "saff.detail.statusChanged": "Statut du compte mis à jour.",
    "saff.detail.confirmDisableTitle": "Désactiver ce compte d'affilié",
    "saff.detail.confirmEnableTitle": "Réactiver ce compte d'affilié",
    "saff.detail.confirmDesc": "La désactivation agit uniquement vers l'avenir : elle arrête les nouvelles attributions et les nouveaux acquis, ne reprend rien et ne gèle rien de ce qui est déjà acquis. Pour reprendre de l'argent, reprenez les commissions une par une, avec un motif.",
    "saff.detail.ack": "J'ai compris que ce changement n'annule aucune commission déjà acquise.",

    "saff.commissions.title": "Commissions",
    "saff.commissions.baseHint": "La base est la commission encaissée par la plateforme sur la vente, jamais le montant de la vente.",
    "saff.commissions.empty": "Aucune commission ne correspond.",
    "saff.commissions.filterAllKinds": "Toutes les origines",
    "saff.commissions.filterAllStates": "Tous les statuts",
    "saff.commissions.colAffiliate": "Affilié",
    "saff.commissions.colCreatedAt": "Date",
    "saff.commissions.colKind": "Origine",
    "saff.commissions.colCounterparty": "Cabinet vendeur",
    "saff.commissions.colBase": "Base",
    "saff.commissions.colRate": "Taux",
    "saff.commissions.colAmount": "Montant",
    "saff.commissions.colReversed": "Repris",
    "saff.commissions.colStatus": "Statut",
    "saff.commissions.colGroup": "Écriture",
    "saff.commissions.colAction": "Action",
    "saff.commissions.reverse": "Reprendre",
    "saff.commissions.ratePct": "{pct} %",

    "saff.reverse.title": "Reprendre cette commission",
    "saff.reverse.desc": "La totalité du solde non repris est reprise : il n'y a pas de reprise partielle par un opérateur. Le motif est obligatoire et reste attaché à l'écriture.",
    "saff.reverse.remainder": "{amount} seront repris à {affiliate}.",
    "saff.reverse.ack": "Je confirme reprendre cette somme à cet affilié.",
    "saff.reverse.reasonPlaceholder": "Motif de la reprise (obligatoire)",

    "saff.rules.title": "Barème d'affiliation",
    "saff.rules.desc": "Consultez le taux applicable aujourd'hui et définissez-en un nouveau s'il change.",
    "saff.rules.inForceTitle": "Taux en vigueur",
    "saff.rules.inForceDesc": "Ce que les affiliés gagnent aujourd'hui, par origine et type de produit.",
    "saff.rules.slot.saas": "Abonnements parrainés",
    "saff.rules.slot.marketplace": "Ventes parrainées",
    "saff.rules.base.saas": "de chaque facture d'abonnement payée par le cabinet parrainé",
    "saff.rules.base.marketplace": "de la commission de Solunary sur la vente",
    "saff.rules.sourceDefault": "Valeur par défaut de la plateforme",
    "saff.rules.sourceRule": "Fixé par {who} le {date}",
    "saff.rules.kind": "Origine",
    "saff.rules.productType": "Type de produit",
    "saff.rules.productTypeAny": "Toutes les ventes (taux par défaut)",
    "saff.rules.rate": "Taux (%)",
    "saff.rules.rateHint": "Soit {bps} points de base.",
    "saff.rules.rateHelp": "Entrez un taux de 0 à 100.",
    "saff.rules.note": "Note",
    "saff.rules.setTitle": "Définir un nouveau taux",
    "saff.rules.setDesc": "S'applique à chaque encaissement à partir de l'enregistrement ; les commissions déjà écrites conservent leur taux.",
    "saff.rules.add": "Enregistrer ce taux",
    "saff.rules.added": "Taux enregistré.",
    "saff.rules.historyTitle": "Historique des taux",
    "saff.rules.filterAllKinds": "Toutes les origines",
    "saff.rules.colKind": "Origine",
    "saff.rules.colProductType": "Type de produit",
    "saff.rules.colRate": "Taux",
    "saff.rules.colFrom": "À partir du",
    "saff.rules.colTo": "Jusqu'au",
    "saff.rules.colSetBy": "Par",
    "saff.rules.colNote": "Note",
    "saff.rules.openBadge": "En vigueur",
    "saff.rules.emptyHistory": "Aucun taux n'a encore été écrit. Les taux ci-dessus sont les valeurs par défaut de la plateforme.",
    "saff.rules.ratePct": "{pct} %",
    "saff.rules.confirmTitle": "Enregistrer ce taux",
    "saff.rules.confirmDesc": "Le taux en vigueur pour cette origine et ce type de produit sera fermé à la date de prise d'effet du nouveau. Les commissions déjà écrites ne changent pas.",
    "saff.rules.ack": "Je confirme que ce taux s'appliquera à tous les encaissements à venir.",

    "saff.policy.title": "Paramètres du programme",
    "saff.policy.desc": "Ces trois valeurs sont lues ensemble par chaque acquisition : elles s'enregistrent ensemble.",
    "saff.policy.enabled": "Programme actif",
    "saff.policy.windowDays": "Fenêtre d'attribution (jours)",
    "saff.policy.windowDaysHint": "De {min} à {max} jours.",
    "saff.policy.saasMonths": "Durée de l'abonnement parrainé (mois)",
    "saff.policy.saasMonthsHint": "De {min} à {max} mois. {min} signifie « la première facture payée uniquement ».",
    "saff.policy.notRetroactive": "Changer une fenêtre ne réécrit rien de ce qui est déjà comptabilisé : la fin de période est figée sur le filleul lors de sa première facture. La fenêtre d'attribution, elle, est jugée à chaque achat ou inscription : plus longue ou plus courte, elle s'applique à toutes les visites pas encore converties.",
    "saff.policy.updatedBy": "Dernière modification par {who} le {when}.",
    "saff.policy.updated": "Dernière modification le {when}.",
    "saff.policy.save": "Enregistrer",
    "saff.policy.saved": "Paramètres enregistrés.",
    "saff.policy.confirmTitle": "Enregistrer les paramètres du programme",
    "saff.policy.confirmDesc": "Ces valeurs décident de ce que gagne chaque affilié à partir de maintenant.",
    "saff.policy.ack": "Je confirme ces trois valeurs.",

    "saff.legacy.title": "Commissions v0 archivées",
    "saff.legacy.desc": "Lecture seule. Ces lignes n'ont aucun lien avec la vente qui les a financées : aucune n'est passée au grand livre, et elles sont conservées telles qu'elles ont été enregistrées (montants décimaux, devise et note d'origine). C'est le document à partir duquel le programme d'avant v1 se solde à la main, une fois.",
    "saff.legacy.colAffiliate": "Affilié",
    "saff.legacy.colType": "Type",
    "saff.legacy.colAmount": "Montant (v0)",
    "saff.legacy.colStatus": "Statut",
    "saff.legacy.colNote": "Note",
    "saff.legacy.colCreatedAt": "Créée le",
    "saff.legacy.colArchivedAt": "Archivée le",
    "saff.legacy.empty": "Aucune commission archivée.",

    "saff.kind.saas": "Abonnement",
    "saff.kind.marketplace": "Vente",

    "saff.state.accrued": "Acquise",
    "saff.state.partially_reversed": "Partiellement reprise",
    "saff.state.reversed": "Reprise",

    "saff.page.info": "{from}–{to} sur {total}",
    "saff.page.prev": "Précédent",
    "saff.page.next": "Suivant",

    "saff.error.AFFILIATE_PROGRAM_DISABLED": "Le programme d'affiliation est suspendu : cette écriture est refusée tant qu'il ne reprend pas.",
    "saff.error.AFFILIATE_CODE_UNKNOWN": "Aucun compte d'affilié ne correspond.",
    "saff.error.AFFILIATE_CODE_TAKEN": "Ce code appartient déjà à un autre compte.",
    "saff.error.AFFILIATE_CODE_INVALID": "Code invalide : de 4 à 16 caractères, minuscules et chiffres uniquement.",
    "saff.error.AFFILIATE_CODE_ALREADY_CHANGED": "Ce code a déjà été changé une fois.",
    "saff.error.AFFILIATE_ACCOUNT_DISABLED": "Ce compte d'affilié est désactivé.",
    "saff.error.AFFILIATE_COMMISSION_NOT_FOUND": "Cette commission est introuvable.",
    "saff.error.AFFILIATE_COMMISSION_ALREADY_REVERSED": "Cette commission a déjà été entièrement reprise : il ne reste rien à reprendre.",
    "saff.error.AFFILIATE_RULE_INVALID": "Ce taux n'est pas valide : vérifiez l'origine et le type de produit (un taux d'abonnement n'en porte aucun).",
    "saff.error.REAUTH_REQUIRED": "Vérification d'identité requise : le jeton a expiré ou a déjà servi.",
    "saff.error.RATE_LIMITED": "Trop de requêtes : réessayez dans un instant.",
    "saff.error.UNKNOWN": "Une erreur est survenue et nous ne pouvons pas en dire plus.",
  },
  en: {
    "nav.affiliate": "Affiliate",

    "saff.title": "Affiliate program",
    "saff.subtitle": "Rates, affiliates, commissions and reversals. Every commission is a balanced ledger group, paid through the wallet.",
    "saff.loading": "Loading…",

    "saff.tab.overview": "Overview",
    "saff.tab.affiliates": "Affiliates",
    "saff.tab.commissions": "Commissions",
    "saff.tab.rules": "Rates",
    "saff.tab.policy": "Policy",
    "saff.tab.legacy": "v0 archive",

    "saff.overview.affiliates": "Affiliates",
    "saff.overview.referralsTotal": "Referrals signed up",
    "saff.overview.linked": "Linked, not yet paying",
    "saff.overview.active": "Active, window running",
    "saff.overview.expired": "Window ended",
    "saff.overview.voided": "Withdrawn",
    "saff.overview.accrued": "Commissions accrued",
    "saff.overview.onHold": "On hold",
    "saff.overview.reversed": "Reversed",
    "saff.overview.clicks30d": "Clicks (30 days)",
    "saff.overview.note": "Every figure here is derived at read time from the ledger-backed collections — none of them is a stored counter. What an affiliate may withdraw is read in their wallet.",

    "saff.affiliates.title": "Affiliates",
    "saff.affiliates.search": "Search",
    "saff.affiliates.searchPlaceholder": "Code or practice id (exact match)",
    "saff.affiliates.filterAllStates": "All statuses",
    "saff.affiliates.colPractice": "Practice",
    "saff.affiliates.colCode": "Code",
    "saff.affiliates.colStatus": "Status",
    "saff.affiliates.colClicks": "Clicks",
    "saff.affiliates.colReferrals": "Referrals",
    "saff.affiliates.colAccrued": "Accrued",
    "saff.affiliates.colReversed": "Reversed",
    "saff.affiliates.colCreatedAt": "Created",
    "saff.affiliates.colAction": "Action",
    "saff.affiliates.view": "Open",
    "saff.affiliates.empty": "No affiliate matches.",

    "saff.accountState.active": "Active",
    "saff.accountState.disabled": "Disabled",

    "saff.detail.back": "Back to the list",
    "saff.detail.enable": "Re-enable this account",
    "saff.detail.disable": "Disable this account",
    "saff.detail.statusChanged": "Account status updated.",
    "saff.detail.confirmDisableTitle": "Disable this affiliate account",
    "saff.detail.confirmEnableTitle": "Re-enable this affiliate account",
    "saff.detail.confirmDesc": "Disabling is forward-only: it stops new attributions and new accruals, reverses nothing and freezes nothing already accrued. To take money back, reverse commissions one at a time, with a recorded reason.",
    "saff.detail.ack": "I understand this does not cancel any commission already accrued.",

    "saff.commissions.title": "Commissions",
    "saff.commissions.baseHint": "The base is the platform's own commission on the sale, never the amount of the sale.",
    "saff.commissions.empty": "No commission matches.",
    "saff.commissions.filterAllKinds": "All sources",
    "saff.commissions.filterAllStates": "All statuses",
    "saff.commissions.colAffiliate": "Affiliate",
    "saff.commissions.colCreatedAt": "Date",
    "saff.commissions.colKind": "Source",
    "saff.commissions.colCounterparty": "Selling practice",
    "saff.commissions.colBase": "Base",
    "saff.commissions.colRate": "Rate",
    "saff.commissions.colAmount": "Amount",
    "saff.commissions.colReversed": "Reversed",
    "saff.commissions.colStatus": "Status",
    "saff.commissions.colGroup": "Ledger group",
    "saff.commissions.colAction": "Action",
    "saff.commissions.reverse": "Reverse",
    "saff.commissions.ratePct": "{pct} %",

    "saff.reverse.title": "Reverse this commission",
    "saff.reverse.desc": "The whole unreversed remainder is taken back: there is no partial admin reversal. The reason is mandatory and is stored on the ledger entry.",
    "saff.reverse.remainder": "{amount} will be taken back from {affiliate}.",
    "saff.reverse.ack": "I confirm taking this amount back from this affiliate.",
    "saff.reverse.reasonPlaceholder": "Reason for the reversal (required)",

    "saff.rules.title": "Affiliate rates",
    "saff.rules.desc": "See the rate that applies today, and set a new one when it changes.",
    "saff.rules.inForceTitle": "Rate in force",
    "saff.rules.inForceDesc": "What affiliates earn today, per source and product type.",
    "saff.rules.slot.saas": "Referred subscriptions",
    "saff.rules.slot.marketplace": "Referred sales",
    "saff.rules.base.saas": "of every subscription invoice the referred practice pays",
    "saff.rules.base.marketplace": "of Solunary's commission on the sale",
    "saff.rules.sourceDefault": "Platform default",
    "saff.rules.sourceRule": "Set by {who} on {date}",
    "saff.rules.kind": "Source",
    "saff.rules.productType": "Product type",
    "saff.rules.productTypeAny": "All sales (default rate)",
    "saff.rules.rate": "Rate (%)",
    "saff.rules.rateHint": "That is {bps} basis points.",
    "saff.rules.rateHelp": "Enter a rate from 0 to 100.",
    "saff.rules.note": "Note",
    "saff.rules.setTitle": "Set a new rate",
    "saff.rules.setDesc": "Applies to every capture from the moment it is saved; commissions already written keep their rate.",
    "saff.rules.add": "Save this rate",
    "saff.rules.added": "Rate saved.",
    "saff.rules.historyTitle": "Rate history",
    "saff.rules.filterAllKinds": "All sources",
    "saff.rules.colKind": "Source",
    "saff.rules.colProductType": "Product type",
    "saff.rules.colRate": "Rate",
    "saff.rules.colFrom": "From",
    "saff.rules.colTo": "Until",
    "saff.rules.colSetBy": "Set by",
    "saff.rules.colNote": "Note",
    "saff.rules.openBadge": "In force",
    "saff.rules.emptyHistory": "No rate has been written yet. The rates above are the platform defaults.",
    "saff.rules.ratePct": "{pct} %",
    "saff.rules.confirmTitle": "Save this rate",
    "saff.rules.confirmDesc": "The rate currently in force for this source and product type will be closed at the new one's effective date. Commissions already written do not change.",
    "saff.rules.ack": "I confirm this rate applies to every future capture.",

    "saff.policy.title": "Program policy",
    "saff.policy.desc": "These three values are read together by every accrual, so they are saved together.",
    "saff.policy.enabled": "Program enabled",
    "saff.policy.windowDays": "Attribution window (days)",
    "saff.policy.windowDaysHint": "{min} to {max} days.",
    "saff.policy.saasMonths": "Referred subscription window (months)",
    "saff.policy.saasMonthsHint": "{min} to {max} months. {min} means the first paid invoice only.",
    "saff.policy.notRetroactive": "Changing a window never restates money already booked: the end of the subscription window is snapshotted on the referral at its first paid invoice. The attribution window is judged at each purchase or signup: longer or shorter, it applies to every visit not yet converted.",
    "saff.policy.updatedBy": "Last changed by {who} on {when}.",
    "saff.policy.updated": "Last changed on {when}.",
    "saff.policy.save": "Save",
    "saff.policy.saved": "Policy saved.",
    "saff.policy.confirmTitle": "Save the program policy",
    "saff.policy.confirmDesc": "These values decide what every affiliate earns from now on.",
    "saff.policy.ack": "I confirm these three values.",

    "saff.legacy.title": "Archived v0 commissions",
    "saff.legacy.desc": "Read-only. These rows have no key back to the sale that funded them: none of them was posted to the ledger, and they are kept exactly as they were stored (decimal amounts, original currency and note). This is the document the pre-v1 program is settled from, by hand, once.",
    "saff.legacy.colAffiliate": "Affiliate",
    "saff.legacy.colType": "Type",
    "saff.legacy.colAmount": "Amount (v0)",
    "saff.legacy.colStatus": "Status",
    "saff.legacy.colNote": "Note",
    "saff.legacy.colCreatedAt": "Created",
    "saff.legacy.colArchivedAt": "Archived",
    "saff.legacy.empty": "No archived commission.",

    "saff.kind.saas": "Subscription",
    "saff.kind.marketplace": "Sale",

    "saff.state.accrued": "Accrued",
    "saff.state.partially_reversed": "Partly reversed",
    "saff.state.reversed": "Reversed",

    "saff.page.info": "{from}–{to} of {total}",
    "saff.page.prev": "Previous",
    "saff.page.next": "Next",

    "saff.error.AFFILIATE_PROGRAM_DISABLED": "The affiliate program is paused: this write is refused until it resumes.",
    "saff.error.AFFILIATE_CODE_UNKNOWN": "No affiliate account matches.",
    "saff.error.AFFILIATE_CODE_TAKEN": "Another account already holds this code.",
    "saff.error.AFFILIATE_CODE_INVALID": "Invalid code: 4 to 16 characters, lowercase letters and digits only.",
    "saff.error.AFFILIATE_CODE_ALREADY_CHANGED": "This code has already been changed once.",
    "saff.error.AFFILIATE_ACCOUNT_DISABLED": "This affiliate account is disabled.",
    "saff.error.AFFILIATE_COMMISSION_NOT_FOUND": "This commission no longer exists.",
    "saff.error.AFFILIATE_COMMISSION_ALREADY_REVERSED": "This commission has already been fully reversed — there is no remainder to take back.",
    "saff.error.AFFILIATE_RULE_INVALID": "That rate is not valid: check the source and the product type (a subscription rate carries none).",
    "saff.error.REAUTH_REQUIRED": "Identity check required: the token expired or has already been used.",
    "saff.error.RATE_LIMITED": "Too many requests — try again in a moment.",
    "saff.error.UNKNOWN": "Something went wrong and we cannot say what.",
  },
  es: {
    "nav.affiliate": "Afiliación",

    "saff.title": "Programa de afiliados",
    "saff.subtitle": "Tasas, afiliados, comisiones y devoluciones. Cada comisión es un asiento equilibrado del libro mayor, pagado por el monedero.",
    "saff.loading": "Cargando…",

    "saff.tab.overview": "Resumen",
    "saff.tab.affiliates": "Afiliados",
    "saff.tab.commissions": "Comisiones",
    "saff.tab.rules": "Tasas",
    "saff.tab.policy": "Parámetros",
    "saff.tab.legacy": "Archivo v0",

    "saff.overview.affiliates": "Afiliados",
    "saff.overview.referralsTotal": "Referidos registrados",
    "saff.overview.linked": "Vinculados, aún sin pago",
    "saff.overview.active": "Activos, periodo en curso",
    "saff.overview.expired": "Periodo finalizado",
    "saff.overview.voided": "Anulados",
    "saff.overview.accrued": "Comisiones ganadas",
    "saff.overview.onHold": "En espera",
    "saff.overview.reversed": "Devueltas",
    "saff.overview.clicks30d": "Clics (30 días)",
    "saff.overview.note": "Todas estas cifras se calculan al leerlas desde el libro mayor: ninguna es un contador almacenado. Lo que un afiliado puede retirar se lee en su monedero.",

    "saff.affiliates.title": "Afiliados",
    "saff.affiliates.search": "Buscar",
    "saff.affiliates.searchPlaceholder": "Código o identificador de consulta (exacto)",
    "saff.affiliates.filterAllStates": "Todos los estados",
    "saff.affiliates.colPractice": "Consulta",
    "saff.affiliates.colCode": "Código",
    "saff.affiliates.colStatus": "Estado",
    "saff.affiliates.colClicks": "Clics",
    "saff.affiliates.colReferrals": "Referidos",
    "saff.affiliates.colAccrued": "Ganado",
    "saff.affiliates.colReversed": "Devuelto",
    "saff.affiliates.colCreatedAt": "Creado",
    "saff.affiliates.colAction": "Acción",
    "saff.affiliates.view": "Abrir",
    "saff.affiliates.empty": "Ningún afiliado coincide.",

    "saff.accountState.active": "Activo",
    "saff.accountState.disabled": "Desactivado",

    "saff.detail.back": "Volver a la lista",
    "saff.detail.enable": "Reactivar esta cuenta",
    "saff.detail.disable": "Desactivar esta cuenta",
    "saff.detail.statusChanged": "Estado de la cuenta actualizado.",
    "saff.detail.confirmDisableTitle": "Desactivar esta cuenta de afiliado",
    "saff.detail.confirmEnableTitle": "Reactivar esta cuenta de afiliado",
    "saff.detail.confirmDesc": "Desactivar solo afecta al futuro: detiene nuevas atribuciones y nuevas comisiones, no devuelve nada y no congela nada ya ganado. Para recuperar dinero, devuelve las comisiones una a una, con un motivo registrado.",
    "saff.detail.ack": "Entiendo que esto no anula ninguna comisión ya ganada.",

    "saff.commissions.title": "Comisiones",
    "saff.commissions.baseHint": "La base es la comisión de la propia plataforma sobre la venta, nunca el importe de la venta.",
    "saff.commissions.empty": "Ninguna comisión coincide.",
    "saff.commissions.filterAllKinds": "Todos los orígenes",
    "saff.commissions.filterAllStates": "Todos los estados",
    "saff.commissions.colAffiliate": "Afiliado",
    "saff.commissions.colCreatedAt": "Fecha",
    "saff.commissions.colKind": "Origen",
    "saff.commissions.colCounterparty": "Consulta vendedora",
    "saff.commissions.colBase": "Base",
    "saff.commissions.colRate": "Tasa",
    "saff.commissions.colAmount": "Importe",
    "saff.commissions.colReversed": "Devuelto",
    "saff.commissions.colStatus": "Estado",
    "saff.commissions.colGroup": "Asiento",
    "saff.commissions.colAction": "Acción",
    "saff.commissions.reverse": "Devolver",
    "saff.commissions.ratePct": "{pct} %",

    "saff.reverse.title": "Devolver esta comisión",
    "saff.reverse.desc": "Se recupera todo el saldo no devuelto: no existe la devolución parcial por parte de un operador. El motivo es obligatorio y queda guardado en el asiento.",
    "saff.reverse.remainder": "Se recuperarán {amount} de {affiliate}.",
    "saff.reverse.ack": "Confirmo que recupero este importe de este afiliado.",
    "saff.reverse.reasonPlaceholder": "Motivo de la devolución (obligatorio)",

    "saff.rules.title": "Tasas de afiliación",
    "saff.rules.desc": "Consulte la tasa aplicable hoy y defina una nueva cuando cambie.",
    "saff.rules.inForceTitle": "Tasa vigente",
    "saff.rules.inForceDesc": "Lo que ganan hoy los afiliados, por origen y tipo de producto.",
    "saff.rules.slot.saas": "Suscripciones referidas",
    "saff.rules.slot.marketplace": "Ventas referidas",
    "saff.rules.base.saas": "de cada factura de suscripción que paga el consultorio referido",
    "saff.rules.base.marketplace": "de la comisión de Solunary sobre la venta",
    "saff.rules.sourceDefault": "Valor por defecto de la plataforma",
    "saff.rules.sourceRule": "Fijada por {who} el {date}",
    "saff.rules.kind": "Origen",
    "saff.rules.productType": "Tipo de producto",
    "saff.rules.productTypeAny": "Todas las ventas (tasa por defecto)",
    "saff.rules.rate": "Tasa (%)",
    "saff.rules.rateHint": "Es decir, {bps} puntos básicos.",
    "saff.rules.rateHelp": "Introduzca una tasa de 0 a 100.",
    "saff.rules.note": "Nota",
    "saff.rules.setTitle": "Definir una nueva tasa",
    "saff.rules.setDesc": "Se aplica a cada cobro desde el momento en que se guarda; las comisiones ya escritas conservan su tasa.",
    "saff.rules.add": "Guardar esta tasa",
    "saff.rules.added": "Tasa guardada.",
    "saff.rules.historyTitle": "Historial de tasas",
    "saff.rules.filterAllKinds": "Todos los orígenes",
    "saff.rules.colKind": "Origen",
    "saff.rules.colProductType": "Tipo de producto",
    "saff.rules.colRate": "Tasa",
    "saff.rules.colFrom": "Desde",
    "saff.rules.colTo": "Hasta",
    "saff.rules.colSetBy": "Fijada por",
    "saff.rules.colNote": "Nota",
    "saff.rules.openBadge": "Vigente",
    "saff.rules.emptyHistory": "Todavía no se ha escrito ninguna tasa. Las tasas anteriores son los valores por defecto de la plataforma.",
    "saff.rules.ratePct": "{pct} %",
    "saff.rules.confirmTitle": "Guardar esta tasa",
    "saff.rules.confirmDesc": "La tasa vigente para este origen y tipo de producto se cerrará en la fecha de efecto de la nueva. Las comisiones ya escritas no cambian.",
    "saff.rules.ack": "Confirmo que esta tasa se aplicará a todos los cobros futuros.",

    "saff.policy.title": "Parámetros del programa",
    "saff.policy.desc": "Cada cálculo lee estos tres valores juntos, así que se guardan juntos.",
    "saff.policy.enabled": "Programa activo",
    "saff.policy.windowDays": "Ventana de atribución (días)",
    "saff.policy.windowDaysHint": "De {min} a {max} días.",
    "saff.policy.saasMonths": "Ventana de la suscripción referida (meses)",
    "saff.policy.saasMonthsHint": "De {min} a {max} meses. {min} significa solo la primera factura pagada.",
    "saff.policy.notRetroactive": "Cambiar una ventana nunca rehace lo ya contabilizado: el fin del periodo queda fijado en el referido con su primera factura pagada. La ventana de atribución se evalúa en cada compra o registro: más larga o más corta, se aplica a todas las visitas aún no convertidas.",
    "saff.policy.updatedBy": "Última modificación por {who} el {when}.",
    "saff.policy.updated": "Última modificación el {when}.",
    "saff.policy.save": "Guardar",
    "saff.policy.saved": "Parámetros guardados.",
    "saff.policy.confirmTitle": "Guardar los parámetros del programa",
    "saff.policy.confirmDesc": "Estos valores deciden lo que gana cada afiliado a partir de ahora.",
    "saff.policy.ack": "Confirmo estos tres valores.",

    "saff.legacy.title": "Comisiones v0 archivadas",
    "saff.legacy.desc": "Solo lectura. Estas filas no tienen ninguna clave hacia la venta que las financió: ninguna se asentó en el libro mayor y se conservan tal como se guardaron (importes decimales, moneda y nota originales). Es el documento con el que se liquida a mano, una vez, el programa anterior a la v1.",
    "saff.legacy.colAffiliate": "Afiliado",
    "saff.legacy.colType": "Tipo",
    "saff.legacy.colAmount": "Importe (v0)",
    "saff.legacy.colStatus": "Estado",
    "saff.legacy.colNote": "Nota",
    "saff.legacy.colCreatedAt": "Creada",
    "saff.legacy.colArchivedAt": "Archivada",
    "saff.legacy.empty": "Ninguna comisión archivada.",

    "saff.kind.saas": "Suscripción",
    "saff.kind.marketplace": "Venta",

    "saff.state.accrued": "Ganada",
    "saff.state.partially_reversed": "Parcialmente devuelta",
    "saff.state.reversed": "Devuelta",

    "saff.page.info": "{from}–{to} de {total}",
    "saff.page.prev": "Anterior",
    "saff.page.next": "Siguiente",

    "saff.error.AFFILIATE_PROGRAM_DISABLED": "El programa de afiliados está en pausa: esta escritura se rechaza hasta que se reanude.",
    "saff.error.AFFILIATE_CODE_UNKNOWN": "Ninguna cuenta de afiliado coincide.",
    "saff.error.AFFILIATE_CODE_TAKEN": "Otra cuenta ya tiene este código.",
    "saff.error.AFFILIATE_CODE_INVALID": "Código no válido: de 4 a 16 caracteres, solo minúsculas y dígitos.",
    "saff.error.AFFILIATE_CODE_ALREADY_CHANGED": "Este código ya se cambió una vez.",
    "saff.error.AFFILIATE_ACCOUNT_DISABLED": "Esta cuenta de afiliado está desactivada.",
    "saff.error.AFFILIATE_COMMISSION_NOT_FOUND": "Esta comisión ya no existe.",
    "saff.error.AFFILIATE_COMMISSION_ALREADY_REVERSED": "Esta comisión ya se ha devuelto por completo: no queda saldo que recuperar.",
    "saff.error.AFFILIATE_RULE_INVALID": "Esa tasa no es válida: revisa el origen y el tipo de producto (una tasa de suscripción no lleva ninguno).",
    "saff.error.REAUTH_REQUIRED": "Se requiere verificación de identidad: el token caducó o ya se usó.",
    "saff.error.RATE_LIMITED": "Demasiadas solicitudes: inténtalo de nuevo en un momento.",
    "saff.error.UNKNOWN": "Algo ha fallado y no podemos decir qué.",
  },
};
